import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import useFormHook from '@shared/hooks/useFormHook';

const TownModal = ({ onClose, onConfirm, editData = null, selectedCountry, selectedRegion, selectedMunicipality }) => {
    const { modalRef } = useFormHook({}, onClose);
    const { t } = useTranslation();
  
    
    const typeOptions = [
        { value: "1", label: t('regions.modal.types.city') },
        { value: "2", label: t('regions.modal.types.village') },
        { value: "3", label: t('regions.modal.types.district') },
        { value: "4", label: t('regions.modal.types.region') },
        { value: "5", label: t('regions.modal.types.complex') },
        { value: "6", label: t('regions.modal.types.resort') },
        { value: "8", label: t('regions.modal.types.monastery') },
        { value: "7", label: t('regions.modal.types.street') },
        { value: "9", label: t('regions.modal.types.boulevard') },
        { value: "10", label: t('regions.modal.types.square') }
    ];

    // Define validation schema based on CityDTO
    const townSchema = yup.object({
        name: yup.string()
            .required(t('validation.city.name.required'))
            .min(1, t('validation.city.name.minLength'))
            .max(60, t('validation.city.name.maxLength')),
        type: yup.string()
            .required(t('validation.city.type.required')),
        transliteration: yup.string(),
        zipCode: yup.string()
            .max(10, t('validation.city.postal.maxLength')),
        ...(selectedCountry.value === "BG" && {
            ekatte: yup.string()
            .nullable()
            .transform((value) => (value === '' ? null : value))
            .test('ekatte-format', t('validation.city.ekatte.format'), function(value) {
                if (!value) return true; // Allow empty/null values
                return /^\d{5}$/.test(value); // Must be exactly 5 digits if provided
            })
        })
    });

    const { register, handleSubmit, formState: { errors } } = useForm({
        resolver: yupResolver(townSchema),
        defaultValues: {
            name: editData?.name || '',
            type: editData?.cityTypes?.id || '',
            transliteration: editData?.transliteration || '',
            zipCode: editData?.postal || '',
            ekatte: editData?.ekatte || ''
        }
    });

    const isEdit = Boolean(editData);
    const title = isEdit ? t('regions.modal.editTown') : t('regions.modal.addTown');

    const onSubmit = (data) => {
        const formData = {
            id: editData?.id,
            name: data.name,
            transliteration: data.transliteration,
            cityTypeId: parseInt(data.type, 10),
            postal: data.zipCode,
            ekatte: data.ekatte,
            countryId: selectedCountry.value,
            regionId: selectedRegion,
            cSubRegionId: selectedMunicipality
        };

        onConfirm(formData);
    };

    return (
        <div className="modal show" tabIndex="-1">
            <div className="modal-dialog modal-confirm" ref={modalRef}>
                <div className="modal-content">
                    <button type="button" className="close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <div className="modal-body">
                        <h4 className="modal-title">{title}</h4>
                        <form id="townForm" className="region-form" onSubmit={handleSubmit(onSubmit)}>
                            <div className="form-group">
                                <span className="span-bold">{t('regions.modal.townName')} <span className="required">*</span></span>
                                <input 
                                    type="text"
                                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                    placeholder={t('regions.modal.townNamePlaceholder')}
                                    {...register('name')}
                                />
                                {errors.name && (
                                    <div className="invalid-feedback">
                                        {errors.name.message}
                                    </div>
                                )}
                            </div>
                            <div className="form-group">
                                <span className="span-bold">{t('regions.modal.type')} <span className="required">*</span></span>
                                <select 
                                    className={`form-control ${errors.type ? 'is-invalid' : ''}`}
                                    {...register('type')}
                                >
                                    <option value="">{t('common.select')}</option>
                                    {typeOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                {errors.type && (
                                    <div className="invalid-feedback">
                                        {errors.type.message}
                                    </div>
                                )}
                            </div>
                            <div className="form-group">
                                <span className="span-bold">{t('regions.modal.townTransliteration')}</span>
                                <input 
                                    type="text"
                                    className={`form-control ${errors.transliteration ? 'is-invalid' : ''}`}
                                    placeholder={t('regions.modal.townTransliterationPlaceholder')}
                                    {...register('transliteration')}
                                />
                                {errors.transliteration && (
                                    <div className="invalid-feedback">
                                        {errors.transliteration.message}
                                    </div>
                                )}
                            </div>
                            <div className="form-group">
                                <span className="span-bold">{t('regions.modal.zipCode')}</span>
                                <input 
                                    type="text"
                                    className={`form-control ${errors.zipCode ? 'is-invalid' : ''}`}
                                    placeholder={t('regions.modal.zipCodePlaceholder')}
                                    {...register('zipCode')}
                                />
                                {errors.zipCode && (
                                    <div className="invalid-feedback">
                                        {errors.zipCode.message}
                                    </div>
                                )}
                            </div>
                            {selectedCountry.value === "BG" && (
                                <div className="form-group">
                                    <span className="span-bold">{t('regions.modal.ekatte')}</span>
                                    <input 
                                        type="text"
                                    className={`form-control ${errors.ekatte ? 'is-invalid' : ''}`}
                                    placeholder={t('regions.modal.ekattePlaceholder')}
                                    {...register('ekatte')}
                                />
                                {errors.ekatte && (
                                    <div className="invalid-feedback">
                                        {errors.ekatte.message}
                                    </div>
                                )}
                            </div>
                            )}
                        </form>
                    </div>
                    <div className="modal-footer">
                        <button type="submit" form="townForm" className="btn btn-primary">
                            {t('common.save')}
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TownModal; 